import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Settings, Copy, Download, Mic, Headphones, X, FolderOpen, FileText } from 'lucide-react';
import { Routes, Route, useNavigate, useLocation, useParams, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthProvider';
import Login from './Login';
import projectService from './services/projectService';
import useWebSocket from './hooks/useWebSocket';
import { useProjectManagement } from './hooks/useProjectManagement';
import { useFileUpload } from './hooks/useFileUpload';
import { useSynopsisGeneration } from './hooks/useSynopsisGeneration';
import { useKeywordGeneration } from './hooks/useKeywordGeneration';
import { useTranscriptionGeneration } from './hooks/useTranscriptionGeneration';
import { useProcessing } from './hooks/useProcessing';
import logger from './utils/logger';
import SynopsisProjectDetailPage from './components/synopsis/SynopsisProjectDetailPage';
import SynopsisComparisonProjectDetailPage from './components/synopsis/SynopsisComparisonProjectDetailPage';
import KeywordProjectDetailPage from './components/keyword/KeywordProjectDetailPage';
import TranscriptionProjectDetailPage from './components/transcription/TranscriptionProjectDetailPage';
import TranscriberProjectDetailModal from './components/transcriber/TranscriberProjectDetailModal';
import TranscriberService from './components/transcriber/TranscriberService';
import SynopsisService from './components/synopsis/SynopsisService';
import KeywordService from './components/keyword/KeywordService';
import TranscriptionService from './components/transcription/TranscriptionService';
import Dashboard from './components/dashboard/Dashboard';
import ProjectsView from './components/projects/ProjectsView';
import AdminPage from './admin/AdminPage';
import UserManagementPage from './admin/UserManagementPage';
import UserDetailPage from './admin/UserDetailPage';
import SynopsisPromptPage from './admin/SynopsisPromptPage';
import KeywordPromptPage from './admin/KeywordPromptPage';
import OrphanedEntriesPage from './admin/OrphanedEntriesPage';
import ProjectStatusPage from './admin/ProjectStatusPage';
import EmailSettingsPage from './admin/EmailSettingsPage';
import AnnouncementSettingPage from './admin/AnnouncementSettingPage';
import Modal from './components/common/Modal';
import ChangePasswordModal from './components/common/ChangePasswordModal';
import { FullPageLoader, Spinner } from './components/ui';
import RootLayout from './components/layout/RootLayout';
import { StatsCardSkeleton } from './components/common/SkeletonLoader';
import DesignSystemPage from './components/design-system/DesignSystemPage';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

logger.log("API Base URL:", API_BASE);

// Route component for Synopsis Project Detail
function SynopsisProjectDetailRoute({ projectManagement, navigate }) {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        const synopsisDetails = await projectService.getSynopsisProjectDetails(projectId);
        const projectData = projectManagement.myProjects.find(p => p.id === projectId);
        
        if (projectData) {
          const projectWithDetails = {
            ...projectData,
            files: synopsisDetails.files || [],
            metadata: synopsisDetails.metadata,
            synopsis: synopsisDetails.synopsis,
            transcript: synopsisDetails.transcript,
            prompt: synopsisDetails.prompt
          };
          setProject(projectWithDetails);
          setDetails(synopsisDetails);
        }
      } catch (error) {
        logger.error('Error loading project:', error);
        navigate('/synopsis/projects');
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      loadProject();
    }
  }, [projectId, navigate, projectManagement.myProjects]);

  const handleClose = () => {
    navigate('/synopsis/projects');
  };

  const handleDelete = async () => {
    if (project) {
      await projectManagement.handleDeleteProject(project);
      navigate('/synopsis/projects');
    }
  };

  const handleRefresh = async () => {
    if (projectId) {
      try {
        const synopsisDetails = await projectService.getSynopsisProjectDetails(projectId);
        setDetails(synopsisDetails);
        const projectData = projectManagement.myProjects.find(p => p.id === projectId);
        if (projectData) {
          const projectWithDetails = {
            ...projectData,
            files: synopsisDetails.files || [],
            metadata: synopsisDetails.metadata,
            synopsis: synopsisDetails.synopsis,
            transcript: synopsisDetails.transcript,
            prompt: synopsisDetails.prompt
          };
          setProject(projectWithDetails);
        }
      } catch (error) {
        logger.error('Error refreshing project:', error);
      }
    }
  };

  if (loading) {
    return <FullPageLoader />;
  }

  if (!project) {
    return null;
  }

  return (
    <SynopsisProjectDetailPage
      project={project}
      details={details}
      onClose={handleClose}
      onDelete={handleDelete}
      onRefresh={handleRefresh}
    />
  );
}

// Route component for Keyword Project Detail
function KeywordProjectDetailRoute({ projectManagement, navigate }) {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        const keywordDetails = await projectService.getKeywordProjectDetails(projectId);
        const projectData = projectManagement.myProjects.find(p => p.id === projectId);
        
        if (projectData) {
          const projectWithDetails = {
            ...projectData,
            files: keywordDetails.files || [],
            metadata: keywordDetails.metadata,
            keywords: keywordDetails.keywords,
            keywords_whisper: keywordDetails.keywords_whisper,
            transcript: keywordDetails.transcript,
            transcript_whisper: keywordDetails.transcript_whisper,
            keyword_assignee_id: keywordDetails.keyword_assignee_id ?? projectData.keyword_assignee_id,
            keyword_assignee_name: keywordDetails.keyword_assignee_name ?? projectData.keyword_assignee_name,
            keyword_approval_status: keywordDetails.keyword_approval_status ?? keywordDetails.project?.keyword_approval_status ?? projectData.keyword_approval_status,
          };
          setProject(projectWithDetails);
          setDetails(keywordDetails);
        }
      } catch (error) {
        logger.error('Error loading keyword project:', error);
        navigate('/keyword/projects');
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      loadProject();
    }
  }, [projectId, navigate, projectManagement.myProjects]);

  const handleClose = () => {
    navigate('/keyword/projects');
  };

  const handleDelete = async (id) => {
    const targetId = id ?? project?.id;
    if (targetId) {
      await projectManagement.handleDeleteProject(targetId);
      navigate('/keyword/projects');
    }
  };

  const handleRefresh = async () => {
    if (projectId) {
      try {
        const keywordDetails = await projectService.getKeywordProjectDetails(projectId);
        setDetails(keywordDetails);
        // Refresh projects list so /projects shows updated approval status when user goes back
        await projectManagement.fetchMyProjects(true);
        const projectData = projectManagement.myProjects.find(p => p.id === projectId);
        if (projectData) {
          const projectWithDetails = {
            ...projectData,
            files: keywordDetails.files || [],
            metadata: keywordDetails.metadata,
            keywords: keywordDetails.keywords,
            keywords_whisper: keywordDetails.keywords_whisper,
            transcript: keywordDetails.transcript,
            transcript_whisper: keywordDetails.transcript_whisper,
            keyword_assignee_id: keywordDetails.keyword_assignee_id ?? projectData.keyword_assignee_id,
            keyword_assignee_name: keywordDetails.keyword_assignee_name ?? projectData.keyword_assignee_name,
            keyword_approval_status: keywordDetails.keyword_approval_status ?? keywordDetails.project?.keyword_approval_status ?? projectData.keyword_approval_status,
          };
          setProject(projectWithDetails);
        } else {
          setProject((prev) => prev ? {
            ...prev,
            keyword_assignee_id: keywordDetails.keyword_assignee_id ?? prev.keyword_assignee_id,
            keyword_assignee_name: keywordDetails.keyword_assignee_name ?? prev.keyword_assignee_name,
            keyword_approval_status: keywordDetails.keyword_approval_status ?? keywordDetails.project?.keyword_approval_status ?? prev.keyword_approval_status,
            transcript: keywordDetails.transcript ?? prev.transcript,
            transcript_whisper: keywordDetails.transcript_whisper ?? prev.transcript_whisper,
            keywords: keywordDetails.keywords ?? prev.keywords,
            keywords_whisper: keywordDetails.keywords_whisper ?? prev.keywords_whisper,
          } : prev);
        }
      } catch (error) {
        logger.error('Error refreshing keyword project:', error);
      }
    }
  };

  if (loading) {
    return <FullPageLoader />;
  }

  if (!project) {
    return null;
  }

  return (
    <KeywordProjectDetailPage
      project={project}
      details={details}
      onClose={handleClose}
      onDelete={handleDelete}
      onRefresh={handleRefresh}
    />
  );
}

// Route component for Transcription Project Detail
function TranscriptionProjectDetailRoute({ projectManagement, navigate }) {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        const transcriptionDetails = await projectService.getTranscriptionProjectDetails(projectId);
        const projectData = projectManagement.myProjects.find(p => p.id === projectId);

        if (projectData) {
          const projectWithDetails = {
            ...projectData,
            files: transcriptionDetails.files || [],
            metadata: transcriptionDetails.metadata,
            keywords: transcriptionDetails.keywords,
            keywords_whisper: transcriptionDetails.keywords_whisper,
            transcript: transcriptionDetails.transcript,
            transcript_whisper: transcriptionDetails.transcript_whisper,
            final_editable_transcript: transcriptionDetails.final_editable_transcript ?? transcriptionDetails.project?.final_editable_transcript ?? projectData.final_editable_transcript,
          };
          setProject(projectWithDetails);
          setDetails(transcriptionDetails);
        }
      } catch (error) {
        logger.error('Error loading transcription project:', error);
        navigate('/transcription/projects');
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      loadProject();
    }
  }, [projectId, navigate, projectManagement.myProjects]);

  const handleClose = () => {
    navigate('/transcription/projects');
  };

  const handleDelete = async (id) => {
    const targetId = id ?? project?.id;
    if (targetId) {
      await projectManagement.handleDeleteProject(targetId);
      navigate('/transcription/projects');
    }
  };

  const handleRefresh = async () => {
    if (projectId) {
      try {
        console.log('[TranscriptionDetail] handleRefresh: fetching transcription-details', projectId);
        const transcriptionDetails = await projectService.getTranscriptionProjectDetails(projectId);
        const fe = transcriptionDetails?.final_editable_transcript ?? transcriptionDetails?.project?.final_editable_transcript;
        const feDebug = fe != null
          ? {
              type: typeof fe,
              transcript_textLength: fe?.transcript_text?.length ?? (typeof fe === 'string' ? fe.length : null),
              hasSegments: Array.isArray(fe?.transcript_segments),
              segmentsCount: fe?.transcript_segments?.length ?? 0,
            }
          : null;
        console.log('[TranscriptionDetail] handleRefresh: API response', {
          hasFinalEditable: fe != null,
          final_editable_transcript: feDebug,
        });
        setDetails(transcriptionDetails);
        await projectManagement.fetchMyProjects(true);
        const projectData = projectManagement.myProjects.find(p => p.id === projectId);
        if (projectData) {
          const finalMerged = transcriptionDetails.final_editable_transcript ?? transcriptionDetails.project?.final_editable_transcript ?? projectData.final_editable_transcript;
          const mergedDebug = finalMerged != null
            ? { transcript_textLength: finalMerged?.transcript_text?.length ?? (typeof finalMerged === 'string' ? finalMerged.length : null), hasSegments: Array.isArray(finalMerged?.transcript_segments) }
            : null;
          console.log('[TranscriptionDetail] handleRefresh: merging into project', { hasFinalMerged: finalMerged != null, merged: mergedDebug });
          const projectWithDetails = {
            ...projectData,
            files: transcriptionDetails.files || [],
            metadata: transcriptionDetails.metadata,
            keywords: transcriptionDetails.keywords,
            keywords_whisper: transcriptionDetails.keywords_whisper,
            transcript: transcriptionDetails.transcript,
            transcript_whisper: transcriptionDetails.transcript_whisper,
            keyword_assignee_id: transcriptionDetails.keyword_assignee_id ?? projectData.keyword_assignee_id,
            keyword_assignee_name: transcriptionDetails.keyword_assignee_name ?? projectData.keyword_assignee_name,
            keyword_approval_status: transcriptionDetails.keyword_approval_status ?? transcriptionDetails.project?.keyword_approval_status ?? projectData.keyword_approval_status,
            final_editable_transcript: finalMerged,
          };
          setProject(projectWithDetails);
        } else {
          console.log('[TranscriptionDetail] handleRefresh: no projectData from fetchMyProjects, updating prev');
          setProject((prev) => prev ? {
            ...prev,
            keyword_assignee_id: transcriptionDetails.keyword_assignee_id ?? prev.keyword_assignee_id,
            keyword_assignee_name: transcriptionDetails.keyword_assignee_name ?? prev.keyword_assignee_name,
            keyword_approval_status: transcriptionDetails.keyword_approval_status ?? transcriptionDetails.project?.keyword_approval_status ?? prev.keyword_approval_status,
            transcript: transcriptionDetails.transcript ?? prev.transcript,
            transcript_whisper: transcriptionDetails.transcript_whisper ?? prev.transcript_whisper,
            keywords: transcriptionDetails.keywords ?? prev.keywords,
            keywords_whisper: transcriptionDetails.keywords_whisper ?? prev.keywords_whisper,
            final_editable_transcript: transcriptionDetails.final_editable_transcript ?? transcriptionDetails.project?.final_editable_transcript ?? prev.final_editable_transcript,
          } : prev);
        }
      } catch (error) {
        console.log('[TranscriptionDetail] handleRefresh error', error);
        logger.error('Error refreshing transcription project:', error);
      }
    }
  };

  if (loading) {
    return <FullPageLoader />;
  }

  if (!project) {
    return null;
  }

  return (
    <TranscriptionProjectDetailPage
      project={project}
      details={details}
      onClose={handleClose}
      onDelete={handleDelete}
      onRefresh={handleRefresh}
    />
  );
}

// Route component for Synopsis Comparison Project Detail
function SynopsisComparisonProjectDetailRoute({ projectManagement, navigate }) {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        const synopsisDetails = await projectService.getSynopsisProjectDetails(projectId);
        const projectData = projectManagement.myProjects.find(p => p.id === projectId);
        
        if (projectData) {
          const projectWithDetails = {
            ...projectData,
            files: synopsisDetails.files || [],
            metadata: synopsisDetails.metadata,
            synopsis: synopsisDetails.synopsis,
            transcript: synopsisDetails.transcript,
            prompt: synopsisDetails.prompt
          };
          setProject(projectWithDetails);
          setDetails(synopsisDetails);
        }
      } catch (error) {
        logger.error('Error loading comparison project:', error);
        navigate('/synopsis/projects');
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      loadProject();
    }
  }, [projectId, navigate, projectManagement.myProjects]);

  const handleClose = () => {
    navigate('/synopsis/projects');
  };

  const handleDelete = async () => {
    if (project) {
      await projectManagement.handleDeleteProject(project);
      navigate('/synopsis/projects');
    }
  };

  const handleRefresh = async () => {
    if (projectId) {
      try {
        const synopsisDetails = await projectService.getSynopsisProjectDetails(projectId);
        setDetails(synopsisDetails);
        const projectData = projectManagement.myProjects.find(p => p.id === projectId);
        if (projectData) {
          const projectWithDetails = {
            ...projectData,
            files: synopsisDetails.files || [],
            metadata: synopsisDetails.metadata,
            synopsis: synopsisDetails.synopsis,
            transcript: synopsisDetails.transcript,
            prompt: synopsisDetails.prompt
          };
          setProject(projectWithDetails);
        }
      } catch (error) {
        logger.error('Error refreshing comparison project:', error);
      }
    }
  };

  if (loading) {
    return <FullPageLoader />;
  }

  if (!project) {
    return null;
  }

  return (
    <SynopsisComparisonProjectDetailPage
      project={project}
      details={details}
      onClose={handleClose}
      onDelete={handleDelete}
      onRefresh={handleRefresh}
    />
  );
}

// Route component for Transcriber Project Detail
function TranscriberProjectDetailRoute({ 
  projectManagement, 
  navigate,
  user,
  isEditingProject,
  editedProjectName,
  editedTranscripts,
  projectNameError,
  startEditingProject,
  saveProjectEdits,
  cancelEditing,
  handleProjectNameChange,
  handleTranscriptChange,
  closeProjectDetail
}) {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        const projectData = projectManagement.myProjects.find(p => p.id === projectId);
        if (projectData) {
          const filesResponse = await projectService.getProjectFiles(projectId);
          const projectWithFiles = {
            ...projectData,
            title: projectData.name || projectData.title,
            files: filesResponse.files || []
          };
          setProject(projectWithFiles);
        }
      } catch (error) {
        logger.error('Error loading project:', error);
        navigate('/transcriber/projects');
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      loadProject();
    }
  }, [projectId, navigate, projectManagement.myProjects]);

  const handleClose = () => {
    navigate('/transcriber/projects');
  };

  if (loading) {
    return <FullPageLoader />;
  }

  if (!project) {
    return null;
  }

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto">
        <Modal
          isOpen={true}
          onClose={handleClose}
          size="xl"
          showCloseButton={false}
        >
          <TranscriberProjectDetailModal
            project={project}
            isEditingProject={isEditingProject}
            editedProjectName={editedProjectName}
            editedTranscripts={editedTranscripts}
            projectNameError={projectNameError}
            onClose={handleClose}
            onStartEditing={startEditingProject}
            onSaveEdits={saveProjectEdits}
            onCancelEditing={cancelEditing}
            onProjectNameChange={handleProjectNameChange}
            onTranscriptChange={handleTranscriptChange}
          />
        </Modal>
      </div>
    </div>
  );
}

function AppContent() {
  const { user, isAuthenticated, logout, authLoading, logoutLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Derive activeService from URL path
  const getActiveServiceFromPath = (pathname) => {
    if (pathname.startsWith('/synopsis/projects/')) return 'synopsis-projects';
    if (pathname.startsWith('/keyword/projects/')) return 'keyword-projects';
    if (pathname.startsWith('/transcription/projects/')) return 'transcription-projects';
    if (pathname.startsWith('/transcriber/projects/')) return 'transcriber-projects';
    if (pathname === '/synopsis/projects') return 'synopsis-projects';
    if (pathname === '/keyword/projects') return 'keyword-projects';
    if (pathname === '/transcription/projects') return 'transcription-projects';
    if (pathname === '/transcriber/projects') return 'transcriber-projects';
    if (pathname === '/synopsis') return 'synopsis';
    if (pathname === '/keyword') return 'keyword';
    if (pathname === '/transcription') return 'transcription';
    if (pathname === '/transcriber') return 'asr';
    if (pathname === '/dashboard' || pathname === '/') return 'dashboard';
    return 'dashboard';
  };
  
  const activeService = getActiveServiceFromPath(location.pathname);
  
  // UI State
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [synopsisExpanded, setSynopsisExpanded] = useState(false);
  const [keywordExpanded, setKeywordExpanded] = useState(false);
  const [transcriptionExpanded, setTranscriptionExpanded] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [setActiveTab] = useState('overview');
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [editedProjectName, setEditedProjectName] = useState('');
  const [editedTranscripts, setEditedTranscripts] = useState({});
  const [projectNameError, setProjectNameError] = useState('');
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  // Navigation helper function
  const setActiveService = (service) => {
    const routeMap = {
      'dashboard': '/dashboard',
      'synopsis': '/synopsis',
      'keyword': '/keyword',
      'transcription': '/transcription',
      'synopsis-projects': '/synopsis/projects',
      'keyword-projects': '/keyword/projects',
      'transcription-projects': '/transcription/projects'
    };
    const route = routeMap[service] || '/dashboard';
    navigate(route);
  };
  
  // Settings State
  const [settings, setSettings] = useState({
    phrases: '',
    language: 'ko-KR',
    boost: 20,
    phraseFile: null,
    usePhraseFile: true
  });
  const [projectName, setProjectName] = useState('');
  const [knpFile, setKnpFile] = useState(null);
  const [transcriptionKnpFile, setTranscriptionKnpFile] = useState(null);
  const [transcriptionLanguage, setTranscriptionLanguage] = useState('ko-KR');

  // Custom Hooks
  const projectManagement = useProjectManagement(user, isAuthenticated);
  const fileUpload = useFileUpload();
  const synopsisGeneration = useSynopsisGeneration();
  const keywordGeneration = useKeywordGeneration();
  const transcriptionGeneration = useTranscriptionGeneration();
  const processing = useProcessing(projectManagement.fetchMyProjects);

  useEffect(() => {
    logger.log(`🔄 Progress state changed to: ${processing.progress}%`);
  }, [processing.progress]);

  useEffect(() => {
    logger.log(`🎯 Active service changed to: ${activeService} (path: ${location.pathname})`);
    // Auto-expand Synopsis section when clicking on synopsis projects
    if (activeService === 'synopsis-projects' || location.pathname.startsWith('/synopsis')) {
      setSynopsisExpanded(true);
    }
    // Auto-expand Keyword section when clicking on keyword projects
    if (activeService === 'keyword-projects' || location.pathname.startsWith('/keyword')) {
      setKeywordExpanded(true);
    }
    // Auto-expand Transcription section when clicking on transcription projects
    if (activeService === 'transcription-projects' || location.pathname.startsWith('/transcription')) {
      setTranscriptionExpanded(true);
    }
  }, [activeService, location.pathname]);

  useEffect(() => {
    if (location.pathname.startsWith('/transcription/projects/') && location.pathname !== '/transcription/projects') {
      setSidebarOpen(false);
    }
  }, [location.pathname]);

  // Force refresh projects once when navigating to Keyword Generation Projects tab (so recently synced projects from Keyword Gen app appear).
  // Depend on pathname and user id only: 'user' object reference often changes every render (auth context), which was re-running this effect and causing constant refresh.
  const userId = user?.id ?? user?.user_id ?? user?.sub ?? null;
  const fetchMyProjectsRef = useRef(projectManagement.fetchMyProjects);
  fetchMyProjectsRef.current = projectManagement.fetchMyProjects;
  useEffect(() => {
    if (location.pathname === '/keyword/projects' && userId && fetchMyProjectsRef.current) {
      fetchMyProjectsRef.current(true);
    }
  }, [location.pathname, userId]);
  useEffect(() => {
    if (location.pathname === '/transcription/projects' && userId && fetchMyProjectsRef.current) {
      fetchMyProjectsRef.current(true);
    }
  }, [location.pathname, userId]);

  // Auto-dismiss completion popup after 10 seconds with countdown
  useEffect(() => {
    if (processing.showCompletionPopup) {
      processing.setPopupCountdown(10);
      
      const countdownInterval = setInterval(() => {
        processing.setPopupCountdown(prev => {
          if (prev <= 1) {
            processing.setShowCompletionPopup(false);
            return 10;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(countdownInterval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processing.showCompletionPopup, processing.setPopupCountdown, processing.setShowCompletionPopup]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-dropdown')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);



  // Close project detail view
  const closeProjectDetail = () => {
    projectManagement.setSelectedProject(null);
    projectManagement.setSynopsisProjectDetails(null);
    projectManagement.setShowProjectDetail(false);
    projectManagement.setShowProjectDetailPage(false);
    setIsEditingProject(false);
    setEditedProjectName('');
    setEditedTranscripts({});
    setActiveTab('overview');
    // Navigate back based on project type
    if (projectManagement.selectedProject) {
      if (projectManagement.selectedProject.projectType === 'synopsis' || 
          projectManagement.selectedProject.projectType === 'synopsis_generation' || 
          projectManagement.selectedProject.projectType === 'synopsis_comparison') {
        navigate('/synopsis/projects');
      } else {
        navigate('/transcriber/projects');
      }
    }
  };

  // Start editing project
  const startEditingProject = () => {
    setIsEditingProject(true);
    setEditedProjectName(projectManagement.selectedProject?.title || projectManagement.selectedProject?.name || '');
    setProjectNameError(''); // Clear any previous validation errors
    // Initialize edited transcripts with current values
    const transcripts = {};
    if (projectManagement.selectedProject?.files) {
      projectManagement.selectedProject.files.forEach(file => {
        if (file.transcripts && file.transcripts.length > 0) {
          transcripts[file.id] = file.transcripts[0].transcript_text || '';
        }
      });
    }
    setEditedTranscripts(transcripts);
  };

  // Cancel editing
  const cancelEditing = () => {
    setIsEditingProject(false);
    setEditedProjectName('');
    setEditedTranscripts({});
    setProjectNameError(''); // Clear validation errors
  };

  // Save project edits
  const saveProjectEdits = async () => {
    try {
      logger.log('💾 Saving project edits...');
      
      // Check for validation errors before saving
      if (projectNameError) {
        alert(`Cannot save: ${projectNameError}`);
        return;
      }
      
      // Update project name if changed
      if (editedProjectName !== projectManagement.selectedProject.title && editedProjectName !== projectManagement.selectedProject.name) {
        logger.log('📝 Updating project name:', editedProjectName);
        
        // Check if the new name already exists (excluding current project)
        const nameExists = projectManagement.myProjects.some(project => 
          project.id !== projectManagement.selectedProject.id && 
          (project.title || project.name) && 
          (project.title || project.name).toLowerCase() === editedProjectName.toLowerCase()
        );
        
        if (nameExists) {
          logger.log(`❌ Project name "${editedProjectName}" already exists.`);
          alert(`Project name "${editedProjectName}" already exists. Please choose a different name.`);
          return; // Prevent save operation
        }
        
        await projectService.updateProject(projectManagement.selectedProject.id, { name: editedProjectName });
      }

      // Update transcripts if changed
      const transcriptUpdates = [];
      if (projectManagement.selectedProject.files) {
        for (const file of projectManagement.selectedProject.files) {
          if (editedTranscripts[file.id] !== undefined && file.transcripts && file.transcripts.length > 0) {
            const currentTranscript = file.transcripts[0].transcript_text || '';
            const newTranscript = editedTranscripts[file.id];
            
            if (currentTranscript !== newTranscript) {
              logger.log('📝 Updating transcript for file:', file.id);
              transcriptUpdates.push(
                projectService.updateTranscript(projectManagement.selectedProject.id, file.transcripts[0].id, {
                  transcript_text: newTranscript
                })
              );
            }
          }
        }
      }

      // Wait for all transcript updates to complete
      if (transcriptUpdates.length > 0) {
        await Promise.all(transcriptUpdates);
      }

      // Refresh project data from server (smart refresh - Redis cache was cleared by backend)
      logger.log('🔄 Refreshing project data...');
      await projectManagement.fetchMyProjects(false);
      
      // Update the selectedProject with the latest data by re-fetching project details
      // This ensures we get the most up-to-date data from the server
      try {
        logger.log('🔄 Re-fetching project details for instant modal update...');
        const filesResponse = await projectService.getProjectFiles(projectManagement.selectedProject.id);
        
        // Get the latest project data from the refreshed myProjects array
        const updatedProject = projectManagement.myProjects.find(p => p.id === projectManagement.selectedProject.id);
        
        if (updatedProject) {
          const projectWithFiles = {
            ...updatedProject,
            title: updatedProject.name || updatedProject.title, // Ensure title field is set for display
            files: filesResponse.files || []
          };
          projectManagement.setSelectedProject(projectWithFiles);
          logger.log('✅ Updated selectedProject with latest project name and transcript data');
        } else {
          logger.log('⚠️ Could not find updated project in myProjects array, using editedProjectName as fallback');
          // Fallback: Update the current selectedProject with the new name from editedProjectName
          const projectWithFiles = {
            ...projectManagement.selectedProject,
            title: editedProjectName || projectManagement.selectedProject.title, // Use the edited name
            name: editedProjectName || projectManagement.selectedProject.name, // Also update the name field
            files: filesResponse.files || []
          };
          projectManagement.setSelectedProject(projectWithFiles);
          logger.log('✅ Updated selectedProject with edited project name as fallback');
        }
      } catch (error) {
        logger.error('❌ Failed to update selectedProject:', error);
      }
      
      // Close edit mode and clear edited state after successful save
      setIsEditingProject(false);
      setEditedProjectName('');
      setEditedTranscripts({});
      setProjectNameError('');
      
      alert('Project updated successfully!');
    } catch (error) {
      logger.error('❌ Failed to save project edits:', error);
      alert('Failed to save changes. Please try again.');
    }
  };

  // Handle transcript text change
  const handleTranscriptChange = (fileId, newText) => {
    setEditedTranscripts(prev => ({
      ...prev,
      [fileId]: newText
    }));
  };

  // Handle project name change with real-time validation
  const handleProjectNameChange = (newName) => {
    setEditedProjectName(newName);
    
    // Clear previous error
    setProjectNameError('');
    
    // Check for duplicate names in real-time
    if (newName.trim()) {
      const nameExists = projectManagement.myProjects.some(project => 
        project.id !== projectManagement.selectedProject.id && 
        (project.title || project.name) && 
        (project.title || project.name).toLowerCase() === newName.toLowerCase()
      );
      
      if (nameExists) {
        setProjectNameError(`Project name "${newName}" already exists`);
      }
    }
  };



  // Removed user WebSocket - using Supabase Realtime instead for better performance

  // WebSocket connection - only connect when we have a valid jobId
  const { isConnected: wsConnected, connectionStatus: wsStatus } = useWebSocket(
    processing.currentJobId && processing.currentJobId !== null ? processing.currentJobId : null,
    processing.handleWebSocketMessage,
    {
      maxReconnectAttempts: 3,
      reconnectInterval: 5000
    }
  );



  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: fileUpload.onDrop,
    accept: {
      'video/mp4': ['.mp4'],
      'video/quicktime': ['.mov'],
      'video/webm': ['.webm'],
      'video/x-matroska': ['.mkv'],
      'video/x-msvideo': ['.avi'],
      'video/3gpp': ['.3gp'],
      'video/x-flv': ['.flv'],
      'audio/wav': ['.wav'],
      'audio/mpeg': ['.mp3'],
      'audio/mp4': ['.m4a'],
      'audio/aac': ['.aac'],
      'audio/flac': ['.flac'],
      'audio/ogg': ['.ogg'],
      'audio/opus': ['.opus'],
      'audio/x-ms-wma': ['.wma'],
      'audio/aiff': ['.aiff', '.aif'],
      'audio/amr': ['.amr'],
    },
    maxFiles: 1,
    multiple: false
  });


  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Transcript copied to clipboard!');
  };

  const downloadTranscript = () => {
    if (processing.result && processing.result.transcript) {
    const blob = new Blob([processing.result.transcript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
      a.download = `${processing.result.original_filename || 'transcript'}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    }
  };

  // Handle viewing synopsis project
  const handleViewSynopsisProject = useCallback(async () => {
    if (!synopsisGeneration.currentSynopsisProjectId) {
      alert('No project ID available');
      return;
    }

    // Find project to check type
    const project = projectManagement.myProjects.find(p => p.id === synopsisGeneration.currentSynopsisProjectId);
    if (project?.projectType === 'synopsis_comparison') {
      navigate(`/synopsis/comparisonprojects/${synopsisGeneration.currentSynopsisProjectId}`);
    } else {
      navigate(`/synopsis/projects/${synopsisGeneration.currentSynopsisProjectId}`);
    }
  }, [synopsisGeneration.currentSynopsisProjectId, navigate, projectManagement.myProjects]);

  const handleViewKeywordProject = useCallback(async () => {
    if (!keywordGeneration.currentKeywordProjectId) {
      alert('No project ID available');
      return;
    }

    navigate(`/keyword/projects/${keywordGeneration.currentKeywordProjectId}`);
  }, [keywordGeneration.currentKeywordProjectId, navigate]);

  const handleViewTranscriptionProject = useCallback(async () => {
    if (!transcriptionGeneration.currentTranscriptionProjectId) {
      alert('No project ID available');
      return;
    }
    navigate(`/transcription/projects/${transcriptionGeneration.currentTranscriptionProjectId}`);
  }, [transcriptionGeneration.currentTranscriptionProjectId, navigate]);
  
  // Wrapper for handleViewProject that uses navigation
  const handleViewProjectWithNavigation = useCallback(async (project) => {
    if (project.projectType === 'synopsis_comparison') {
      navigate(`/synopsis/comparisonprojects/${project.id}`);
    } else if (project.type === 'synopsis' || project.projectType === 'synopsis' || project.projectType === 'synopsis_generation' || project.projectType === 'synopsis_comparison') {
      navigate(`/synopsis/projects/${project.id}`);
    } else if (project.type === 'keyword' || project.projectType === 'keyword' || project.projectType === 'keyword_generation') {
      navigate(`/keyword/projects/${project.id}`);
    } else if (project.type === 'transcription_generation' || project.projectType === 'transcription_generation') {
      navigate(`/transcription/projects/${project.id}`);
    } else {
      navigate(`/transcriber/projects/${project.id}`);
    }
  }, [navigate]);


  // Show login if not authenticated
  if (!isAuthenticated()) {
    return <Login />;
  }

  // Show authentication loading overlay with skeleton UI
  if (authLoading) {
    return (
      <div className="h-screen bg-gray-50 dark:bg-gray-950 overflow-hidden">
        <div className="flex h-full">
          {/* Sidebar Skeleton */}
          <div className="w-64 bg-white dark:bg-neutral-950 border-r border-gray-200 dark:border-neutral-800 flex flex-col">
            {/* Header Skeleton */}
            <div className="p-4 border-b border-gray-200 dark:border-neutral-800 h-16">
              <div className="animate-pulse">
                <div className="h-6 bg-gray-200 dark:bg-neutral-800 rounded w-24"></div>
              </div>
            </div>
            
            {/* Menu Items Skeleton */}
            <div className="flex-1 p-3 space-y-2">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="h-10 bg-gray-200 dark:bg-neutral-800 rounded-lg"></div>
                </div>
              ))}
            </div>
            
            {/* User Info Skeleton */}
            <div className="p-3 border-t border-gray-200 dark:border-neutral-800">
              <div className="flex items-center space-x-2 animate-pulse">
                <div className="w-8 h-8 bg-gray-200 dark:bg-neutral-800 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 dark:bg-neutral-800 rounded w-24 mb-1"></div>
                  <div className="h-3 bg-gray-200 dark:bg-neutral-800 rounded w-32"></div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Main Content Skeleton */}
          <div className="flex-1 flex flex-col">
            {/* Header Skeleton */}
            <div className="bg-white dark:bg-neutral-950 border-b border-gray-200 dark:border-neutral-800 px-6 py-3 h-14">
              <div className="flex items-center justify-between animate-pulse">
                <div className="h-6 bg-gray-200 dark:bg-neutral-800 rounded w-48"></div>
                <div className="h-8 w-8 bg-gray-200 dark:bg-neutral-800 rounded-full"></div>
              </div>
            </div>
            
            {/* Content Area Skeleton */}
            <div className="flex-1 overflow-auto p-6">
              <div className="max-w-7xl mx-auto space-y-6">
                {/* Stats Cards Skeleton */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <StatsCardSkeleton />
                  <StatsCardSkeleton />
                  <StatsCardSkeleton />
                  <StatsCardSkeleton />
                </div>
                
                {/* Loading Message */}
                <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg p-6 text-center">
                  <div className="flex justify-center mb-4">
                    <Spinner size="xl" className="text-primary" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-neutral-100 mb-2">Loading your workspace...</h3>
                  <p className="text-sm text-gray-600 dark:text-neutral-400">Authenticating and fetching projects</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show logout loading overlay
  if (logoutLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-black flex items-center justify-center">
        <div className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg shadow-lg p-8 max-w-md w-full mx-4">
          <div className="text-center space-y-6">
            <div className="flex justify-center">
              <Spinner size="xl" className="text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-2xl font-medium text-gray-900 mb-2">Signing out...</h3>
              <p className="text-gray-600">Please wait while we securely log you out</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-gray-600 rounded-full animate-pulse"></div>
                <span>Clearing session data</span>
              </div>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-gray-600 rounded-full animate-pulse"></div>
                <span>Revoking access tokens</span>
              </div>
              <div className="flex items-center justify-center space-x-2 text-sm text-gray-500">
                <div className="w-2 h-2 bg-gray-300 rounded-full"></div>
                <span>Redirecting to login</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <ChangePasswordModal />
      <RootLayout
        sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      activeService={activeService}
      setActiveService={setActiveService}
      synopsisExpanded={synopsisExpanded}
      setSynopsisExpanded={setSynopsisExpanded}
      keywordExpanded={keywordExpanded}
      setKeywordExpanded={setKeywordExpanded}
      transcriptionExpanded={transcriptionExpanded}
      setTranscriptionExpanded={setTranscriptionExpanded}
      setShowProjectDetailPage={projectManagement.setShowProjectDetailPage}
      setSelectedProject={projectManagement.setSelectedProject}
      setSynopsisProjectDetails={projectManagement.setSynopsisProjectDetails}
      user={user}
      showUserDropdown={showUserDropdown}
      setShowUserDropdown={setShowUserDropdown}
      logout={logout}
      logoutLoading={logoutLoading}
      showSidebar={!location.pathname.startsWith('/admin')}
    >
      <>
      <Routes>
            {/* Synopsis Project Detail Route */}
            <Route path="/synopsis/projects/:projectId" element={
              <SynopsisProjectDetailRoute
                projectManagement={projectManagement}
                navigate={navigate}
              />
            } />

            {/* Synopsis Comparison Project Detail Route */}
            <Route path="/synopsis/comparisonprojects/:projectId" element={
              <SynopsisComparisonProjectDetailRoute 
                projectManagement={projectManagement} 
                navigate={navigate} 
              />
            } />

            {/* Keyword Project Detail Route */}
            <Route path="/keyword/projects/:projectId" element={
              <KeywordProjectDetailRoute
                projectManagement={projectManagement}
                navigate={navigate}
              />
            } />

            {/* Transcription Project Detail Route */}
            <Route path="/transcription/projects/:projectId" element={
              <TranscriptionProjectDetailRoute
                projectManagement={projectManagement}
                navigate={navigate}
              />
            } />
            
            {/* Transcriber Project Detail Route */}
            <Route path="/transcriber/projects/:projectId" element={
              <TranscriberProjectDetailRoute
                projectManagement={projectManagement}
                navigate={navigate}
                user={user}
                isEditingProject={isEditingProject}
                editedProjectName={editedProjectName}
                editedTranscripts={editedTranscripts}
                projectNameError={projectNameError}
                startEditingProject={startEditingProject}
                saveProjectEdits={saveProjectEdits}
                cancelEditing={cancelEditing}
                handleProjectNameChange={handleProjectNameChange}
                handleTranscriptChange={handleTranscriptChange}
                closeProjectDetail={closeProjectDetail}
              />
            } />

            {/* Admin Page Route */}
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/users" element={<UserManagementPage />} />
            <Route path="/admin/users/:userId" element={<UserDetailPage />} />
            <Route path="/admin/grant_access" element={<UserManagementPage />} />
            <Route path="/admin/synopsys_prompt" element={<SynopsisPromptPage />} />
            <Route path="/admin/keyword_prompt" element={<KeywordPromptPage />} />
            <Route path="/admin/project_status" element={<ProjectStatusPage />} />
            <Route path="/admin/orphaned_entries" element={<OrphanedEntriesPage />} />
            <Route path="/admin/email_settings" element={<EmailSettingsPage />} />
            <Route path="/admin/announcement_settings" element={<AnnouncementSettingPage />} />

            {/* Main Layout Routes */}
            <Route path="*" element={
              <div className={`flex flex-1 flex-col overflow-hidden ${processing.isProcessing && !processing.isProcessingMinimized ? 'opacity-40' : 'opacity-100'}`}>
                <div className="flex-1 px-4 sm:px-6 py-6 sm:py-8 overflow-y-auto">
                  <Routes>
                    {/* Dashboard Route */}
                    <Route path="/design-system" element={<DesignSystemPage />} />
                    <Route path="/dashboard" element={
                      <Dashboard
                        myProjects={projectManagement.myProjects}
                        projectsLoading={projectManagement.projectsLoading}
                        isProcessing={processing.isProcessing}
                        setActiveService={setActiveService}
                        handleViewProject={handleViewProjectWithNavigation}
                        user={user}
                      />
                    } />
                    
                    {/* Transcriber Service Route */}
                    <Route path="/transcriber" element={
                      <TranscriberService
                        projectName={projectName}
                        setProjectName={setProjectName}
                        knpFile={knpFile}
                        setKnpFile={setKnpFile}
                        selectedFiles={fileUpload.selectedFiles}
                        setSelectedFiles={fileUpload.setSelectedFiles}
                        driveUrl={fileUpload.driveUrl}
                        setDriveUrl={fileUpload.setDriveUrl}
                        driveFileInfo={fileUpload.driveFileInfo}
                        setDriveFileInfo={fileUpload.setDriveFileInfo}
                        validatingDriveUrl={fileUpload.validatingDriveUrl}
                        setValidatingDriveUrl={fileUpload.setValidatingDriveUrl}
                        validationErrors={fileUpload.validationErrors}
                        setValidationErrors={fileUpload.setValidationErrors}
                        isProcessing={processing.isProcessing}
                        getRootProps={getRootProps}
                        getInputProps={getInputProps}
                        isDragActive={isDragActive}
                        error={processing.error}
                        checkProjectNameExists={projectManagement.checkProjectNameExists}
                        handleProcessFiles={(assigneeId, inputSource) => processing.handleProcessFiles(
                          projectName,
                          fileUpload.selectedFiles,
                          fileUpload.driveUrl,
                          settings,
                          knpFile,
                          () => fileUpload.validateForm(projectName, projectManagement.checkProjectNameExists),
                          assigneeId,
                          inputSource
                        )}
                        setShowSettingsModal={setShowSettingsModal}
                        user={user}
                      />
                    } />
                    
                    {/* Synopsis Service Route */}
                    <Route path="/synopsis" element={
                      <SynopsisService
                        synopsisYoutubeUrl={synopsisGeneration.synopsisYoutubeUrl}
                        setSynopsisYoutubeUrl={synopsisGeneration.setSynopsisYoutubeUrl}
                        youtubeVideoInfo={synopsisGeneration.youtubeVideoInfo}
                        extractingVideoInfo={synopsisGeneration.extractingVideoInfo}
                        generatedSynopsis={synopsisGeneration.generatedSynopsis}
                        currentSynopsisProjectId={synopsisGeneration.currentSynopsisProjectId}
                        isProcessing={processing.isProcessing}
                        handleSynopsisProcessing={(url, info, version, setGen, setProj, redir, show, mp4, type, text, assigneeId) => processing.handleSynopsisProcessing(
                          url,
                          info,
                          version,
                          setGen,
                          setProj,
                          (service) => {
                            // Redirect callback
                            setActiveService(service);
                            // Scroll to top
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          },
                          show,
                          mp4,
                          type,
                          text,
                          assigneeId
                        )}
                        handleViewSynopsisProject={handleViewSynopsisProject}
                        // Prompt management props
                        synopsisPrompt={synopsisGeneration.synopsisPrompt}
                        synopsisPromptVersion={synopsisGeneration.synopsisPromptVersion}
                        synopsisPromptTotalVersions={synopsisGeneration.synopsisPromptTotalVersions}
                        selectedPromptVersionForUse={synopsisGeneration.selectedPromptVersionForUse}
                        isEditingSynopsisPrompt={synopsisGeneration.isEditingSynopsisPrompt}
                        editedSynopsisPrompt={synopsisGeneration.editedSynopsisPrompt}
                        synopsisPromptLoading={synopsisGeneration.synopsisPromptLoading}
                        setIsEditingSynopsisPrompt={synopsisGeneration.setIsEditingSynopsisPrompt}
                        setEditedSynopsisPrompt={synopsisGeneration.setEditedSynopsisPrompt}
                        handleSynopsisPromptVersionChange={synopsisGeneration.handleSynopsisPromptVersionChange}
                        saveSynopsisPrompt={synopsisGeneration.saveSynopsisPrompt}
                        setSelectedPromptVersionForUse={synopsisGeneration.setSelectedPromptVersionForUse}
                        fetchSynopsisPromptVersion={synopsisGeneration.fetchSynopsisPromptVersion}
                        selectedPromptId={synopsisGeneration.selectedPromptId}
                        fetchSynopsisPrompt={synopsisGeneration.fetchSynopsisPrompt}
                        user={user}
                      />
                    } />
                    
                    {/* Keyword Service Route */}
                    <Route path="/keyword" element={
                      <KeywordService
                        keywordYoutubeUrl={keywordGeneration.keywordYoutubeUrl}
                        setKeywordYoutubeUrl={keywordGeneration.setKeywordYoutubeUrl}
                        youtubeVideoInfo={keywordGeneration.youtubeVideoInfo}
                        extractingVideoInfo={keywordGeneration.extractingVideoInfo}
                        generatedKeywords={keywordGeneration.generatedKeywords}
                        currentKeywordProjectId={keywordGeneration.currentKeywordProjectId}
                        isProcessing={processing.isProcessing}
                        handleKeywordProcessing={(url, info, version, setGen, setProj, redir, show, mp4, type, text, assigneeId) => processing.handleContentProcessing(
                          'keyword',
                          url,
                          info,
                          version,
                          setGen,
                          setProj,
                          (service) => {
                            setActiveService(service);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          },
                          'keyword-projects',
                          show,
                          mp4,
                          type,
                          text,
                          assigneeId
                        )}
                        handleViewKeywordProject={handleViewKeywordProject}
                        user={user}
                      />
                    } />

                    {/* Transcription Service Route */}
                    <Route path="/transcription" element={
                      <TranscriptionService
                        transcriptionYoutubeUrl={transcriptionGeneration.transcriptionYoutubeUrl}
                        setTranscriptionYoutubeUrl={transcriptionGeneration.setTranscriptionYoutubeUrl}
                        youtubeVideoInfo={transcriptionGeneration.youtubeVideoInfo}
                        setYoutubeVideoInfo={transcriptionGeneration.setYoutubeVideoInfo}
                        extractingVideoInfo={transcriptionGeneration.extractingVideoInfo}
                        generatedTranscription={transcriptionGeneration.generatedTranscription}
                        currentTranscriptionProjectId={transcriptionGeneration.currentTranscriptionProjectId}
                        isProcessing={processing.isProcessing}
                        handleTranscriptionProcessing={(url, info, version, setGen, setProj, redir, show, mp4, type, text, assigneeId, knpFileParam, languageCodeParam) => processing.handleContentProcessing(
                          'transcription',
                          url,
                          info,
                          version,
                          setGen,
                          setProj,
                          (service) => {
                            setActiveService(service);
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          },
                          'transcription-projects',
                          show,
                          mp4,
                          type,
                          text,
                          assigneeId,
                          knpFileParam,
                          languageCodeParam
                        )}
                        transcriptionKnpFile={transcriptionKnpFile}
                        setTranscriptionKnpFile={setTranscriptionKnpFile}
                        transcriptionLanguage={transcriptionLanguage}
                        setTranscriptionLanguage={setTranscriptionLanguage}
                        handleViewTranscriptionProject={handleViewTranscriptionProject}
                        user={user}
                      />
                    } />
                    
                    {/* Transcriber Projects Route */}
                    <Route path="/transcriber/projects" element={
                      <ProjectsView
                        activeService="transcriber-projects"
                        myProjects={projectManagement.myProjects}
                        projectsLoading={projectManagement.projectsLoading}
                        sortField={projectManagement.sortField}
                        sortDirection={projectManagement.sortDirection}
                        fetchMyProjects={projectManagement.fetchMyProjects}
                        setActiveService={setActiveService}
                        handleSort={projectManagement.handleSort}
                        handleViewProject={handleViewProjectWithNavigation}
                        handleDeleteProject={projectManagement.handleDeleteProject}
                        sortProjects={projectManagement.sortProjects}
                        user={user}
                      />
                    } />
                    
                    {/* Synopsis Projects Route */}
                    <Route path="/synopsis/projects" element={
                      <ProjectsView
                        activeService="synopsis-projects"
                        myProjects={projectManagement.myProjects}
                        projectsLoading={projectManagement.projectsLoading}
                        sortField={projectManagement.sortField}
                        sortDirection={projectManagement.sortDirection}
                        fetchMyProjects={projectManagement.fetchMyProjects}
                        setActiveService={setActiveService}
                        handleSort={projectManagement.handleSort}
                        handleViewProject={handleViewProjectWithNavigation}
                        handleDeleteProject={projectManagement.handleDeleteProject}
                        sortProjects={projectManagement.sortProjects}
                        user={user}
                      />
                    } />
                    
                    {/* Keyword Projects Route */}
                    <Route path="/keyword/projects" element={
                      <ProjectsView
                        activeService="keyword-projects"
                        myProjects={projectManagement.myProjects}
                        projectsLoading={projectManagement.projectsLoading}
                        sortField={projectManagement.sortField}
                        sortDirection={projectManagement.sortDirection}
                        fetchMyProjects={projectManagement.fetchMyProjects}
                        setActiveService={setActiveService}
                        handleSort={projectManagement.handleSort}
                        handleViewProject={handleViewProjectWithNavigation}
                        handleDeleteProject={projectManagement.handleDeleteProject}
                        sortProjects={projectManagement.sortProjects}
                        user={user}
                      />
                    } />

                    {/* Transcription Projects Route */}
                    <Route path="/transcription/projects" element={
                      <ProjectsView
                        activeService="transcription-projects"
                        myProjects={projectManagement.myProjects}
                        projectsLoading={projectManagement.projectsLoading}
                        sortField={projectManagement.sortField}
                        sortDirection={projectManagement.sortDirection}
                        fetchMyProjects={projectManagement.fetchMyProjects}
                        setActiveService={setActiveService}
                        handleSort={projectManagement.handleSort}
                        handleViewProject={handleViewProjectWithNavigation}
                        handleDeleteProject={projectManagement.handleDeleteProject}
                        sortProjects={projectManagement.sortProjects}
                        user={user}
                      />
                    } />
                    
                    {/* Default redirect to dashboard */}
                    <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  </Routes>
                  
                  {/* Settings Modal */}
                  {showSettingsModal && (
              <div 
                className="fixed inset-0 bg-gray-900 bg-opacity-90 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
                onClick={() => setShowSettingsModal(false)}
              >
                <div 
                  className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl transform transition-all duration-300 ease-out"
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Modal Header */}
                  <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center">
                        <Settings className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">Settings</h2>
                        <p className="text-sm text-gray-500">Configure transcription preferences</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowSettingsModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>

                  {/* Modal Content */}
                  <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Language */}
                      <div>
                        <label htmlFor="language" className="block text-gray-700 text-sm font-medium mb-2">
                          Language
                        </label>
                        <select
                          id="language"
                          className="w-full bg-gray-100 border border-gray-200 rounded-lg px-4 py-3 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:border-transparent"
                          value={settings.language}
                          onChange={(e) => setSettings({ ...settings, language: e.target.value })}
                        >
                          <option value="en-US">English (US)</option>
                          <option value="ko-KR">Korean</option>
                          <option value="ja-JP">Japanese</option>
                          <option value="zh-CN">Chinese (Mandarin)</option>
                        </select>
                      </div>

                      {/* Boost Level */}
                      <div>
                        <label htmlFor="boost" className="block text-gray-700 text-sm font-medium mb-2">
                          Boost Level: {settings.boost}
                        </label>
                        <input
                          type="range"
                          id="boost"
                          min="0"
                          max="20"
                          value={settings.boost}
                          onChange={(e) => setSettings({ ...settings, boost: parseFloat(e.target.value) })}
                          className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                        />
                        <div className="flex justify-between text-gray-500 text-xs mt-1">
                          <span>0</span>
                          <span>20</span>
                        </div>
                      </div>
                    </div>

                    
                  </div>

                  {/* Modal Footer */}
                  <div className="flex items-center justify-end p-6 border-t border-gray-200">
                    <button
                      onClick={() => setShowSettingsModal(false)}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}

                </div>
              </div>
            } />
          </Routes>

        {/* Processing Status Modal */}
      {processing.isProcessing && (
          <div className={`fixed ${processing.isProcessingMinimized ? 'top-4 right-4 w-80' : 'inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center'} z-[70]`}>
            <div className={`bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-lg shadow-2xl transform transition-all duration-300 ease-out ${
              processing.isProcessingMinimized ? 'p-4' : 'p-8 max-w-md w-full mx-4'
            }`}>
            <div className={`text-center ${processing.isProcessingMinimized ? 'space-y-2' : 'space-y-6'}`}>
              {/* Minimize/Maximize Button */}
              <div className="flex justify-end mb-2">
                <button
                  onClick={() => processing.setIsProcessingMinimized(!processing.isProcessingMinimized)}
                  className="text-gray-400 hover:text-gray-600 dark:text-neutral-400 dark:hover:text-neutral-300 transition-colors"
                  title={processing.isProcessingMinimized ? 'Maximize' : 'Minimize'}
                >
                  {processing.isProcessingMinimized ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                    </svg>
                  )}
                </button>
              </div>
              
              <div className="flex justify-center">
                  <Mic className={`text-gray-600 dark:text-neutral-400 animate-pulse ${processing.isProcessingMinimized ? 'w-8 h-8' : 'w-16 h-16'}`} />
              </div>
                <h3 className={`font-medium text-gray-900 dark:text-neutral-100 ${processing.isProcessingMinimized ? 'text-sm' : 'text-2xl'}`}>
                  {processing.isProcessingMinimized ? 'Processing...' : 'Processing Audio...'}
                </h3>
                
                {/* WebSocket Status Indicator - Only show when not minimized */}
                {processing.currentJobId && !processing.isProcessingMinimized && (
                  <div className="flex items-center justify-center space-x-2 text-sm">
                    <div className={`w-2 h-2 rounded-full ${
                      wsConnected ? 'bg-green-500' : 
                      wsStatus === 'connecting' ? 'bg-yellow-500 animate-pulse' : 
                      'bg-red-500'
                    }`}></div>
                    <span className={`text-xs ${
                      wsConnected ? 'text-green-600' : 
                      wsStatus === 'connecting' ? 'text-yellow-600' : 
                      'text-red-600'
                    }`}>
                      {wsConnected ? 'Real-time updates' : 
                       wsStatus === 'connecting' ? 'Connecting...' : 
                       'Polling mode'}
                    </span>
                  </div>
                )}
              <div className="relative">
                  <div className={`progress-bar bg-gray-200 dark:bg-neutral-800 rounded-full overflow-hidden shadow-inner ${processing.isProcessingMinimized ? 'h-2' : 'h-4'}`}>
                  <div 
                      className="progress-fill bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-500 ease-out relative"
                    style={{ width: `${processing.progress}%` }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 animate-pulse"></div>
                  </div>
                </div>
                  <div className="absolute inset-0 rounded-full border-2 border-gray-300 dark:border-neutral-700 pointer-events-none"></div>
              </div>
              
              <div className={`flex justify-between items-center ${processing.isProcessingMinimized ? 'text-sm' : 'text-lg'}`}>
                  <span className="text-gray-900 dark:text-neutral-100 truncate">{processing.progressMessage}</span>
                  <span className="text-gray-600 dark:text-neutral-400 font-bold">{processing.progress}%</span>
              </div>
              
              {/* Duration and ETA Information - Only show when not minimized */}
              {(processing.duration || processing.eta) && !processing.isProcessingMinimized && (
                <div className="space-y-4">
                  {processing.duration && (
                      <div className="bg-gray-50 dark:bg-neutral-900 p-4 rounded-lg border border-gray-200 dark:border-neutral-800">
                        <div className="text-sm text-gray-600 dark:text-neutral-400 mb-1">Audio Duration</div>
                        <div className="text-xl font-medium text-gray-900 dark:text-neutral-100">
                        {Math.floor(processing.duration / 60)}:{(processing.duration % 60).toFixed(0).padStart(2, '0')}
                      </div>
                    </div>
                  )}
                  {processing.eta && (
                      <div className="bg-gray-50 dark:bg-neutral-900 p-4 rounded-lg border border-gray-200 dark:border-neutral-800">
                        <div className="text-sm text-gray-600 dark:text-neutral-400 mb-1">Estimated Completion</div>
                        <div className="text-xl font-medium text-gray-900 dark:text-neutral-100">{processing.eta}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Completion Popup Notification */}
      {processing.showCompletionPopup && (
        <div 
          className="fixed inset-0 bg-gray-900 bg-opacity-90 backdrop-blur-sm flex items-center justify-center z-[60] p-4"
          onClick={() => processing.setShowCompletionPopup(false)}
        >
          <div 
            className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-xl max-w-md w-full mx-4 shadow-2xl transform transition-all duration-300 ease-out"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Popup Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Transcription Complete!</h3>
                    <p className="text-green-100 text-sm">Your audio has been successfully processed</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-green-100 text-sm">
                    Auto-close in {processing.popupCountdown}s
                  </span>
                  <button
                    onClick={() => processing.setShowCompletionPopup(false)}
                    className="text-white hover:text-green-200 transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>
            </div>

            {/* Popup Content */}
            <div className="p-6">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Headphones className="w-8 h-8 text-green-600" />
                </div>
                
                <div>
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">
                    {processing.completionPopupData.title || 'Project Completed'}
                  </h4>
                  <p className="text-gray-600 text-sm mb-4">
                    {processing.completionPopupData.message || 'Your transcription is ready for review and download.'}
                  </p>
                  {processing.completionPopupData.projectName && (
                    <div className="bg-gray-50 rounded-lg p-3 mb-4">
                      <p className="text-sm text-gray-700">
                        <span className="font-medium">Project:</span> {processing.completionPopupData.projectName}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      processing.setShowCompletionPopup(false);
                      setActiveService('transcriber-projects');
                    }}
                    className="flex-1 bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center"
                  >
                    <FolderOpen className="w-4 h-4 mr-2" />
                    View Projects
                  </button>
                  <button
                    onClick={() => processing.setShowCompletionPopup(false)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    Continue Working
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transcript Results Popup */}
      {processing.showTranscriptPopup && processing.result && (
        <div 
          className="fixed inset-0 bg-gray-900 bg-opacity-90 backdrop-blur-sm flex items-center justify-center z-[70] p-4"
          onClick={() => processing.setShowTranscriptPopup(false)}
        >
          <div 
            className="bg-white dark:bg-neutral-950 border border-gray-200 dark:border-neutral-800 rounded-xl max-w-4xl w-full mx-4 max-h-[90vh] shadow-2xl transform transition-all duration-300 ease-out flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Check if it's a synopsis result - look for synopsis-specific fields */}
            {processing.result.youtube_url || processing.result.video_id || (processing.result.title && !processing.result.transcript) ? (
              <>
                {/* Synopsis Popup Header */}
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-t-xl flex-shrink-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                        <FileText className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">Synopsis Results</h3>
                        <p className="text-sky-100 text-sm">Your synopsis is ready</p>
                      </div>
                    </div>
                    <button
                      onClick={() => processing.setShowTranscriptPopup(false)}
                      className="text-white hover:text-sky-200 transition-colors"
                    >
                      <X className="w-6 h-6" />
                    </button>
                  </div>
                </div>

                {/* Synopsis Popup Content */}
                <div className="flex-1 overflow-y-auto p-6">
                  <div className="space-y-6">
                    {/* Action Buttons */}
                    <div className="flex justify-center space-x-4">
                      <button
                        onClick={() => copyToClipboard(processing.result.synopsis_text || processing.result.synopsis)}
                        disabled={!processing.result.synopsis_text && !processing.result.synopsis}
                        className="bg-gray-100 hover:bg-gray-200 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed text-gray-800 px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center border border-gray-200"
                      >
                        <Copy className="w-5 h-5 mr-2" />
                        Copy Synopsis
                      </button>
                      <button
                        onClick={() => {
                          const synopsisText = processing.result.synopsis_text || processing.result.synopsis;
                          const blob = new Blob([synopsisText], { type: 'text/plain' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `${processing.result.title || 'synopsis'}.txt`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        }}
                        disabled={!processing.result.synopsis_text && !processing.result.synopsis}
                        className="bg-sky-600 hover:bg-sky-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
                      >
                        <Download className="w-5 h-5 mr-2" />
                        Download
                      </button>
                    </div>
                    
                    {/* Video Info */}
                    {processing.result.title && (
                      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Video Title</h4>
                        <p className="text-gray-900 font-medium">{processing.result.title}</p>
                        {processing.result.youtube_url && (
                          <a 
                            href={processing.result.youtube_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sky-600 hover:text-sky-700 text-sm mt-2 inline-block"
                          >
                            View on YouTube →
                          </a>
                        )}
                      </div>
                    )}
                    
                    {/* Synopsis Content */}
                    <div>
                      <h4 className="text-lg font-semibold mb-4 text-gray-900">Generated Synopsis</h4>
                      <div className="bg-gray-50 p-6 rounded-lg max-h-96 overflow-y-auto border border-gray-200">
                        {(processing.result.synopsis_text || processing.result.synopsis) ? (
                          <p className="whitespace-pre-wrap text-base leading-relaxed text-gray-800">
                            {processing.result.synopsis_text || processing.result.synopsis}
                          </p>
                        ) : (
                          <div className="text-center py-8">
                            <div className="text-red-600 mb-2">
                              <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                            </div>
                            <p className="text-gray-700 font-medium mb-2">Synopsis generation failed</p>
                            <p className="text-sm text-gray-500 mb-4">
                              Gemini API is not configured. Please set up your Gemini API key.
                            </p>
                            <p className="text-xs text-gray-400">
                              Check the backend logs for more details or see backend/GEMINI_SETUP.md for setup instructions.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Description Preview */}
                    {processing.result.description && (
                      <div>
                        <h4 className="text-lg font-semibold mb-4 text-gray-900">Video Description</h4>
                        <div className="bg-gray-50 p-4 rounded-lg max-h-48 overflow-y-auto border border-gray-200">
                          <p className="whitespace-pre-wrap text-sm leading-relaxed text-gray-800">
                            {processing.result.description}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Synopsis Popup Footer */}
                <div className="flex items-center justify-end p-6 border-t border-gray-200 flex-shrink-0">
                  <div className="flex space-x-3">
                    <button
                      onClick={() => {
                        processing.setShowTranscriptPopup(false);
                        setActiveService('synopsis-projects');
                      }}
                      className="bg-sky-600 hover:bg-sky-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                    >
                      <FolderOpen className="w-4 h-4 mr-2" />
                      View Projects
                    </button>
                    <button
                      onClick={() => processing.setShowTranscriptPopup(false)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Transcript Popup Header */}
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-6 rounded-t-xl flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                    <Headphones className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Transcript Results</h3>
                    <p className="text-sky-100 text-sm">Your transcription is ready</p>
                  </div>
                </div>
                <button
                  onClick={() => processing.setShowTranscriptPopup(false)}
                  className="text-white hover:text-sky-200 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

                {/* Transcript Popup Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Action Buttons */}
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => copyToClipboard(processing.result.transcript)}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center border border-gray-200"
                  >
                    <Copy className="w-5 h-5 mr-2" />
                    Copy Transcript
                  </button>
                  <button
                    onClick={downloadTranscript}
                    className="bg-sky-600 hover:bg-sky-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center"
                  >
                    <Download className="w-5 h-5 mr-2" />
                    Download
                  </button>
                </div>
                
                {/* Transcript Content */}
                <div>
                  <h4 className="text-lg font-semibold mb-4 text-gray-900">Transcript</h4>
                  <div className="bg-gray-50 p-6 rounded-lg max-h-96 overflow-y-auto border border-gray-200">
                    <p className="whitespace-pre-wrap text-base leading-relaxed text-gray-800">
                      {processing.result.transcript}
                    </p>
                  </div>
                </div>
                
                {/* Statistics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
                    <div className="text-xl font-bold text-gray-900 mb-1">
                      {(processing.result.confidence || 0).toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-600">Confidence</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
                    <div className="text-xl font-bold text-gray-900 mb-1">
                      {processing.result.language_code || 'Unknown'}
                    </div>
                    <div className="text-sm text-gray-600">Language</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
                    <div className="text-xl font-bold text-gray-900 mb-1">
                      {processing.result.boost || 0}
                    </div>
                    <div className="text-sm text-gray-600">Boost Level</div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 text-center">
                    <div className="text-xl font-bold text-gray-900 mb-1">
                      {processing.result.phrases?.length || 0}
                    </div>
                    <div className="text-sm text-gray-600">Custom Phrases</div>
                  </div>
                </div>
              </div>
            </div>

                {/* Transcript Popup Footer */}
            <div className="flex items-center justify-end p-6 border-t border-gray-200 flex-shrink-0">
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    processing.setShowTranscriptPopup(false);
                        setActiveService('transcriber-projects');
                  }}
                  className="bg-sky-600 hover:bg-sky-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors flex items-center"
                >
                  <FolderOpen className="w-4 h-4 mr-2" />
                  View Projects
                </button>
                <button
                  onClick={() => processing.setShowTranscriptPopup(false)}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
              </>
            )}
          </div>
        </div>
      )}
      </>
    </RootLayout>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
